import React, { useState, useEffect } from "react";
import Swal from "sweetalert2"; // For beautiful alerts like your screenshot

const AssignPrice = () => {
  /* ------------------- STATE ------------------- */
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]); // Store all fetched accounts
  const [priceGroups, setPriceGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [userLabId, setUserLabId] = useState(null);

  /* ------------------- FETCH USER LAB ID ------------------- */
  useEffect(() => {
    const labId = localStorage.getItem("userLabId");
    setUserLabId(labId);
  }, []);

  /* ------------------- FETCH DATA ------------------- */
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        // Always use the general accounts endpoint
        const accountsUrl = "https://hms.backend.hiragroupcloud.com/api/account/account";

        // Build groups URL with query parameter if userLabId exists
        let groupsUrl = "https://hms.backend.hiragroupcloud.com/api/prices/get";
        if (userLabId) {
          groupsUrl = `https://hms.backend.hiragroupcloud.com/api/prices/get?userLabId=${encodeURIComponent(userLabId)}`;
        }

        const [accountsRes, groupsRes] = await Promise.all([
          fetch(accountsUrl),
          fetch(groupsUrl),
        ]);

        if (!accountsRes.ok || !groupsRes.ok) {
          throw new Error(
            `HTTP error! Accounts: ${accountsRes.status}, Groups: ${groupsRes.status}`
          );
        }

        const accountsData = await accountsRes.json();
        const groupsRaw = await groupsRes.json();

        if (!accountsData.data || !Array.isArray(accountsData.data)) {
          throw new Error("Invalid accounts data format from API");
        }

        if (!Array.isArray(groupsRaw)) {
          throw new Error("Invalid price groups data format from API");
        }

        // Store all accounts
        const allAccountsData = accountsData.data;
        setAllAccounts(allAccountsData);

        /* ---- Filter accounts based on userLabId (frontend filtering) ---- */
        let filteredAccounts = allAccountsData;
        
        if (userLabId) {
          filteredAccounts = allAccountsData.filter(
            (account) => account.addedBy === userLabId
          );
        }

        /* ---- Format accounts for display ---- */
        const formattedAccounts = filteredAccounts.map((account) => {
          const accountGroups = account.priceGroups
            ? account.priceGroups
                .filter((g) => g && typeof g === "object" && "name" in g)
                .map((g) => g.name)
            : [];

          return {
            id: account._id,
            name: account.account_name || `Account ${account.accountId}`,
            accountId: account.accountId,
            city: account.city || "",
            email: account.email || "",
            telephone: account.telephone || "",
            status: account.status || "active",
            addedBy: account.addedBy || "",
            selected: `${accountGroups.length} Selected`,
            group: `(${accountGroups.join(", ")})`,
            groups: accountGroups,
          };
        });

        /* ---- Sanitize groups ---- */
        const sanitizedGroups = groupsRaw.map((group) => ({
          ...group,
          members: Array.isArray(group.members) ? group.members : [],
        }));

        setAccounts(formattedAccounts);
        setPriceGroups(sanitizedGroups);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [userLabId]);

  /* ------------------- BACKEND UPDATE ------------------- */
  const updateGroupInBackend = async (groupName, accountId, action) => {
    const group = priceGroups.find((g) => g.name === groupName);
    if (!group) throw new Error(`Group ${groupName} not found`);

    let updateUrl = `https://hms.backend.hiragroupcloud.com/api/prices/${group._id}`;
    if (userLabId) {
      updateUrl += `?userLabId=${encodeURIComponent(userLabId)}`;
    }

    const response = await fetch(updateUrl, {
      method: "PUT",
      headers: { 
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ 
        action: action,
        accountId: accountId
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `Failed to ${action} member: ${errorText}`;
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorMessage;
      } catch (e) {}
      
      throw new Error(errorMessage);
    }

    const updatedGroup = await response.json();
    
    setPriceGroups((prev) =>
      prev.map((g) => (g._id === group._id ? updatedGroup : g))
    );
    
    return updatedGroup;
  };

  /* ------------------- MODAL HANDLERS ------------------- */
  const handleLinkClick = (account) => {
    setSelectedAccount(account);
    const accountGroups = account.groups || [];
    setSelectedGroups(accountGroups);

    const allNames = priceGroups.filter((g) => g.name).map((g) => g.name);
    setAvailableGroups(allNames.filter((n) => !accountGroups.includes(n)));

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAccount(null);
    setSelectedGroups([]);
    setAvailableGroups([]);
  };

  const handleOK = () => closeModal();

  /* ------------------- DRAG & DROP ------------------- */
  const handleDragStart = (e, item, source) => {
    setDraggedItem({ item, source });
    e.dataTransfer.setData("text/plain", item);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = async (e, target) => {
    e.preventDefault();
    if (!draggedItem || !selectedAccount) return;
    
    const { item: groupName, source } = draggedItem;
    if (source === target) return;

    const action = source === "available" && target === "selected" ? "add" : "remove";

    setSaving(true);
    try {
      await updateGroupInBackend(groupName, selectedAccount.id, action);

      if (action === "add") {
        setSelectedGroups((s) => [...s, groupName]);
        setAvailableGroups((a) => a.filter((g) => g !== groupName));
      } else {
        setSelectedGroups((s) => s.filter((g) => g !== groupName));
        setAvailableGroups((a) => [...a, groupName]);
      }

      const newSel = action === "add"
        ? [...selectedGroups, groupName]
        : selectedGroups.filter((g) => g !== groupName);

      const updatedAcc = {
        ...selectedAccount,
        groups: newSel,
        selected: `${newSel.length} Selected`,
        group: `(${newSel.join(", ")})`,
      };
      
      setAccounts((prev) =>
        prev.map((a) => (a.id === selectedAccount.id ? updatedAcc : a))
      );
      setSelectedAccount(updatedAcc);

      // Success alert after drag-drop - same style as your image
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: `Group ${action === "add" ? "added" : "removed"} successfully!`,
        confirmButtonText: "OK",
        confirmButtonColor: "#004aad",
      });

    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: err.message,
        confirmButtonText: "OK",
        confirmButtonColor: "#004aad",
      });
    } finally {
      setSaving(false);
      setDraggedItem(null);
    }
  };

  /* ------------------- REFRESH DATA ------------------- */
  const handleSave = async () => {
    try {
      const accountsUrl = "https://hms.backend.hiragroupcloud.com/api/account/account";

      let groupsUrl = "https://hms.backend.hiragroupcloud.com/api/prices/get";
      if (userLabId) {
        groupsUrl = `https://hms.backend.hiragroupcloud.com/api/prices/get?userLabId=${encodeURIComponent(userLabId)}`;
      }

      const [accountsRes, groupsRes] = await Promise.all([
        fetch(accountsUrl),
        fetch(groupsUrl),
      ]);
      
      if (!accountsRes.ok || !groupsRes.ok) throw new Error("Refresh failed");

      const accountsData = await accountsRes.json();
      const groupsRaw = await groupsRes.json();

      if (!accountsData.data || !Array.isArray(accountsData.data)) {
        throw new Error("Invalid accounts data format from API");
      }

      const allAccountsData = accountsData.data;
      setAllAccounts(allAccountsData);

      let filteredAccounts = allAccountsData;
      if (userLabId) {
        filteredAccounts = allAccountsData.filter(
          (account) => account.addedBy === userLabId
        );
      }

      const formatted = filteredAccounts.map((account) => {
        const accountGroups = account.priceGroups
          ? account.priceGroups.filter((g) => g && g.name).map((g) => g.name)
          : [];

        return {
          id: account._id,
          name: account.account_name || `Account ${account.accountId}`,
          accountId: account.accountId,
          city: account.city || "",
          email: account.email || "",
          telephone: account.telephone || "",
          status: account.status || "active",
          addedBy: account.addedBy || "",
          selected: `${accountGroups.length} Selected`,
          group: `(${accountGroups.join(", ")})`,
          groups: accountGroups,
        };
      });

      setAccounts(formatted);
      
      const sanitizedGroups = groupsRaw.map((group) => ({
        ...group,
        members: Array.isArray(group.members) ? group.members : [],
      }));
      setPriceGroups(sanitizedGroups);
      
      // EXACT SAME SWEETALERT AS YOUR IMAGE - Clean, no background color
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Data refreshed and saved successfully!",
        confirmButtonText: "OK",
        confirmButtonColor: "#004aad",
      });

    } catch (err) {
      Swal.fire({
        icon: "warning",
        title: "Partial Success",
        text: "Changes saved locally – could not refresh from server.",
        confirmButtonText: "OK",
        confirmButtonColor: "#004aad",
      });
    }
  };

  /* ------------------- RENDER ------------------- */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
          color: "#004aad",
        }}
      >
        Loading accounts and groups...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <div style={{ fontSize: "18px", color: "red" }}>Error loading data: {error}</div>
        <button
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: "#004aad",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <>
      {/* ---------- PAGE LAYOUT ---------- */}
      <div style={{ width: "95%", margin: "0 auto", padding: "0px" }}>
        <div className="row">
          <div className="col-md-10">
            <div
              style={{
                backgroundColor: "#ffffff",
                minHeight: "100vh",
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                padding: "20px 40px",
                color: "#333",
              }}
            >
              <h1 style={{ color: "#000000", marginBottom: "10px" }}>
                Assign Price List Group To Accounts
              </h1>
              <div
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "20px 30px",
                  maxWidth: "1000px",
                  marginBottom: "20px",
                  position: "relative",
                  borderTop: "2px solid #004aad",
                }}
              >
                {accounts.length > 0 ? (
                  accounts.map((acc) => (
                    <div
                      key={acc.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: "13px",
                        padding: "6px 0",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#000",
                          width: "370px",
                          display: "inline-block",
                        }}
                      >
                        {acc.name} ({acc.accountId})
                      </span>
                      <span
                        onClick={() => handleLinkClick(acc)}
                        style={{
                          color: "#1a73e8",
                          cursor: "pointer",
                          textDecoration: "none",
                          marginRight: "4px",
                        }}
                        onMouseOver={(e) => (e.target.style.textDecoration = "underline")}
                        onMouseOut={(e) => (e.target.style.textDecoration = "none")}
                      >
                        {acc.selected}
                      </span>
                      <span style={{ color: "#000" }}>{acc.group}</span>
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                    {userLabId 
                      ? `No accounts found for Lab ID: ${userLabId}. There are ${allAccounts.length} total accounts in the system.`
                      : "No accounts found"}
                  </div>
                )}

                <button
                  onClick={handleSave}
                  style={{
                    backgroundColor: "#004aad",
                    border: "1px solid #004aad",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    marginTop: "10px",
                    color: "#ffffff",
                  }}
                  onMouseOver={(e) => (e.target.style.backgroundColor = "#003a8a")}
                  onMouseOut={(e) => (e.target.style.backgroundColor = "#004aad")}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "12px",
                      height: "12px",
                      background: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="%23ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>') no-repeat center`,
                      marginRight: "4px",
                    }}
                  ></span>
                  Refresh & Save
                </button>
              </div>

              {/* ---------- MODAL ---------- */}
              {isModalOpen && selectedAccount && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000,
                  }}
                  onClick={closeModal}
                >
                  <div
                    style={{
                      backgroundColor: "white",
                      padding: "25px",
                      borderRadius: "8px",
                      width: "90%",
                      maxWidth: "800px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      style={{
                        backgroundColor: "#004aad",
                        color: "white",
                        padding: "10px 20px",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        margin: "-25px -25px 15px -25px",
                      }}
                    >
                      <span style={{ fontSize: "16px", fontWeight: "500" }}>
                        Select Pricelist Groups
                      </span>
                      <span
                        style={{
                          cursor: "pointer",
                          fontSize: "18px",
                          fontWeight: "bold",
                        }}
                        onClick={closeModal}
                      >
                        x
                      </span>
                    </div>

                    <h4
                      style={{
                        color: "#000000",
                        fontWeight: "500",
                        fontSize: "18px",
                        marginBottom: "20px",
                      }}
                    >
                      Assign Pricelist Groups For {selectedAccount.name} ({selectedAccount.accountId})
                    </h4>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "20px",
                        alignItems: "start",
                      }}
                    >
                      {/* SELECTED */}
                      <div>
                        <div
                          style={{
                            color: "#000000",
                            fontWeight: "500",
                            fontSize: "14px",
                            marginBottom: "8px",
                          }}
                        >
                          Selected Pricelist Groups
                        </div>
                        <div
                          style={{
                            backgroundColor: "#ffffff",
                            padding: "15px",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                            minHeight: "200px",
                            maxHeight: "200px",
                            overflowY: "scroll",
                            fontSize: "14px",
                          }}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, "selected")}
                        >
                          {selectedGroups.length > 0 ? (
                            selectedGroups.map((group, i) => (
                              <div
                                key={i}
                                draggable
                                onDragStart={(e) => handleDragStart(e, group, "selected")}
                                style={{
                                  padding: "8px 5px",
                                  borderBottom:
                                    i < selectedGroups.length - 1
                                      ? "1px solid #e0e0e0"
                                      : "none",
                                  backgroundColor:
                                    group === "default" ? "#d9eaf4" : "transparent",
                                  cursor: "grab",
                                }}
                                onMouseOver={(e) =>
                                  (e.target.style.backgroundColor = "#d9eaf4")
                                }
                                onMouseOut={(e) => {
                                  if (group !== "default")
                                    e.target.style.backgroundColor = "transparent";
                                }}
                              >
                                {group}
                                {group === "default" && " (default group)"}
                              </div>
                            ))
                          ) : (
                            <div style={{ padding: "8px 5px", color: "#666" }}>
                              No groups selected
                            </div>
                          )}
                        </div>
                      </div>

                      {/* AVAILABLE */}
                      <div>
                        <div
                          style={{
                            color: "#000000",
                            fontWeight: "500",
                            fontSize: "14px",
                            marginBottom: "8px",
                          }}
                        >
                          Available Pricelist Groups
                        </div>
                        <div
                          style={{
                            backgroundColor: "#ffffff",
                            padding: "15px",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                            minHeight: "200px",
                            maxHeight: "200px",
                            overflowY: "scroll",
                            fontSize: "14px",
                          }}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, "available")}
                        >
                          {availableGroups.length > 0 ? (
                            availableGroups.map((group, i) => (
                              <div
                                key={i}
                                draggable
                                onDragStart={(e) => handleDragStart(e, group, "available")}
                                style={{
                                  padding: "8px 5px",
                                  borderBottom:
                                    i < availableGroups.length - 1
                                      ? "1px solid #e0e0e0"
                                      : "none",
                                  cursor: "grab",
                                }}
                                onMouseOver={(e) =>
                                  (e.target.style.backgroundColor = "#d9eaf4")
                                }
                                onMouseOut={(e) =>
                                  (e.target.style.backgroundColor = "transparent")
                                }
                              >
                                {group}
                              </div>
                            ))
                          ) : (
                            <div style={{ padding: "8px 5px", color: "#666" }}>
                              No groups available
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "20px",
                        gap: "10px",
                      }}
                    >
                      <button
                        onClick={closeModal}
                        disabled={saving}
                        style={{
                          backgroundColor: saving ? "#ccc" : "#004aad",
                          color: "white",
                          border: "none",
                          padding: "8px 20px",
                          borderRadius: "4px",
                          cursor: saving ? "not-allowed" : "pointer",
                          fontSize: "14px",
                        }}
                        onMouseOver={(e) =>
                          !saving && (e.target.style.backgroundColor = "#003a8a")
                        }
                        onMouseOut={(e) =>
                          !saving && (e.target.style.backgroundColor = "#004aad")
                        }
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleOK}
                        disabled={saving}
                        style={{
                          backgroundColor: saving ? "#ccc" : "#004aad",
                          color: "white",
                          border: "none",
                          padding: "8px 20px",
                          borderRadius: "4px",
                          cursor: saving ? "not-allowed" : "pointer",
                          fontSize: "14px",
                        }}
                        onMouseOver={(e) =>
                          !saving && (e.target.style.backgroundColor = "#003a8a")
                        }
                        onMouseOut={(e) =>
                          !saving && (e.target.style.backgroundColor = "#004aad")
                        }
                      >
                        {saving ? "Saving..." : "OK"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignPrice;